import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const compactUsd = (v) => `$${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0)}`;
import { formatCurrency } from "@/lib/format";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, Minus, Loader2, Store, Trophy, AlertTriangle } from "lucide-react";

const RANGES = [6, 9, 12];
const MIX_COLORS = ["#2563eb", "#059669", "#f59e0b", "#94a3b8"];

/** One client's 12-month profit, orders and markup story on a single screen. */
export default function ClientProfitStory({ accountId, onBack }) {
  const [months, setMonths] = useState(12);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/profit-report/client/${accountId}?months=${months}`)
      .then(({ data }) => setD(data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, [accountId, months]);

  const series = d?.series || [];
  const t = d?.totals;
  const mixTotal = (t?.revenue_mix || []).reduce((a, x) => a + (x.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="client-profit-story">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button onClick={onBack} data-testid="client-story-back"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> All clients
            </button>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }} data-testid="client-story-name">
              {d?.account?.company_name || "…"}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{d?.account?.boxhero_account_id}</span>
              {(d?.account?.stores || []).length > 0 && (
                <span className="inline-flex items-center gap-1.5"><Store className="w-3.5 h-3.5" />
                  {d.account.stores.length} store{d.account.stores.length > 1 ? "s" : ""} · {d.account.stores.join(", ")}
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                {d?.account?.costs_source === "account" ? "own cost card" : "global costs"}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-1.5" data-testid="client-story-range">
            {RANGES.map((n) => (
              <button key={n} onClick={() => setMonths(n)} data-testid={`client-story-months-${n}`}
                className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${months === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                {n} mo
              </button>
            ))}
          </div>
        </div>

        {t && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mt-4" data-testid="client-story-tiles">
              <Tile label={`Profit · last ${t.months} complete months`} value={formatCurrency(t.profit)} accent />
              <Tile label="Margin" value={`${t.margin_pct}%`} delta={d.comparable ? d.deltas.margin_pts : null} suffix=" pts" dir={d.deltas.direction} />
              <Tile label="Shipping markup" value={`${t.markup_pct_effective}%`} delta={d.comparable ? d.deltas.markup_pts : null} suffix=" pts" dir={d.deltas.markup_pts >= 0 ? "up" : "down"} />
              <Tile label="Orders / month" value={t.avg_orders_per_month.toLocaleString()} delta={d.comparable ? d.deltas.orders_pct : null} suffix="%" dir={d.deltas.orders_pct >= 0 ? "up" : "down"} />
              <Tile label="Profit / order" value={formatCurrency(t.avg_profit_per_order)} />
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              {d.best_month && (
                <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1" data-testid="client-story-best">
                  <Trophy className="w-3.5 h-3.5" /> Best month {d.best_month.label} · {formatCurrency(d.best_month.profit)}
                </span>
              )}
              {d.weakest_month && (
                <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1" data-testid="client-story-weakest">
                  <AlertTriangle className="w-3.5 h-3.5" /> Thinnest margin {d.weakest_month.label} · {d.weakest_month.margin_pct}%
                </span>
              )}
              {t.credits_given > 0 && (
                <span className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-900 rounded-full px-3 py-1">
                  Credits given − {formatCurrency(t.credits_given)}
                </span>
              )}
            </div>
          </>
        )}

        <div className="h-72 mt-5" data-testid="client-story-chart">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => compactUsd(v)} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => (n.includes("%") ? `${v}%` : n === "Orders" ? v : formatCurrency(v))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="l" dataKey="profit" name="Profit" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={38} />
                <Bar yAxisId="l" dataKey="cost" name="Our cost" fill="#cbd5e1" radius={[4, 4, 0, 0]} maxBarSize={38} />
                <Line yAxisId="r" type="monotone" dataKey="orders" name="Orders" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line yAxisId="r" type="monotone" dataKey="markup_pct_effective" name="Markup %" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {mixTotal > 0 && (
          <div className="mt-4" data-testid="client-story-mix">
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Where the revenue comes from</p>
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
              {t.revenue_mix.filter((x) => x.amount > 0).map((x, i) => (
                <div key={x.label} title={`${x.label}: ${formatCurrency(x.amount)}`}
                  style={{ width: `${(x.amount / mixTotal) * 100}%`, background: MIX_COLORS[i % MIX_COLORS.length] }} />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-600">
              {t.revenue_mix.filter((x) => x.amount > 0).map((x, i) => (
                <span key={x.label} className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm" style={{ background: MIX_COLORS[i % MIX_COLORS.length] }} />
                  {x.label} {formatCurrency(x.amount)} · {Math.round((x.amount / mixTotal) * 100)}%
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="client-story-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2.5 px-4">Month</th>
              <th className="py-2.5 px-4 text-right">Orders</th>
              <th className="py-2.5 px-4 text-right">Units</th>
              <th className="py-2.5 px-4 text-right">Shipping billed</th>
              <th className="py-2.5 px-4 text-right">Carrier cost</th>
              <th className="py-2.5 px-4 text-right">Markup</th>
              <th className="py-2.5 px-4 text-right">Fulfillment profit</th>
              <th className="py-2.5 px-4 text-right">Packaging profit</th>
              <th className="py-2.5 px-4 text-right">Credits</th>
              <th className="py-2.5 px-4 text-right">Revenue</th>
              <th className="py-2.5 px-4 text-right">Profit</th>
              <th className="py-2.5 px-4 text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            {series.map((r) => (
              <tr key={r.label} data-testid={`client-story-row-${r.year}-${r.month}`}
                className={`border-t border-slate-100 ${r.calculated ? "hover:bg-slate-50/60" : "text-slate-400"}`}>
                <td className="py-2.5 px-4 font-semibold text-slate-900">
                  {r.label}
                  {r.partial && <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">so far</span>}
                </td>
                {!r.calculated ? (
                  <td colSpan={11} className="py-2.5 px-4 text-xs">Not calculated — use “Fill in the history” on the trend</td>
                ) : (
                  <>
                    <td className="py-2.5 px-4 text-right">{r.orders.toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right">{r.units.toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right">{formatCurrency(r.shipping_revenue)}</td>
                    <td className="py-2.5 px-4 text-right text-slate-500">{formatCurrency(r.shipping_cost)}</td>
                    <td className="py-2.5 px-4 text-right">{r.markup_pct_effective == null ? <span className="text-slate-400" title="No carrier cost recorded for this month">n/a</span> : `${r.markup_pct_effective}%`}</td>
                    <td className="py-2.5 px-4 text-right">{formatCurrency(r.fulfillment_profit)}</td>
                    <td className="py-2.5 px-4 text-right">{r.packaging_revenue ? formatCurrency(r.packaging_profit) : "—"}</td>
                    <td className="py-2.5 px-4 text-right text-orange-700">{r.credits_given ? `− ${formatCurrency(r.credits_given)}` : "—"}</td>
                    <td className="py-2.5 px-4 text-right">{formatCurrency(r.revenue)}</td>
                    <td className="py-2.5 px-4 text-right font-bold text-slate-900">{formatCurrency(r.profit)}</td>
                    <td className="py-2.5 px-4 text-right">{r.margin_pct}%</td>
                  </>
                )}
              </tr>
            ))}
            {t && t.months > 0 && (
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                <td className="py-2.5 px-4">{t.months} complete months</td>
                <td className="py-2.5 px-4 text-right">{t.orders.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right">{t.units.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.shipping_revenue)}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.shipping_cost)}</td>
                <td className="py-2.5 px-4 text-right">{t.markup_pct_effective}%</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.fulfillment_profit)}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.packaging_profit)}</td>
                <td className="py-2.5 px-4 text-right text-orange-700">{t.credits_given ? `− ${formatCurrency(t.credits_given)}` : "—"}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.revenue)}</td>
                <td className="py-2.5 px-4 text-right">{formatCurrency(t.profit)}</td>
                <td className="py-2.5 px-4 text-right">{t.margin_pct}%</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Totals cover complete months only — the month in progress is shown in the table but left out of the averages.
      </p>
    </div>
  );
}

function Tile({ label, value, accent, delta, suffix = "", dir }) {
  const flat = delta === null || delta === undefined || Math.abs(delta) < 0.1;
  const Icon = flat ? Minus : dir === "up" ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={`rounded-lg p-3 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
      {delta !== null && delta !== undefined && (
        <p className={`text-xs font-semibold inline-flex items-center gap-1 ${flat ? "text-slate-400" : dir === "up" ? "text-emerald-700" : "text-red-600"}`}>
          <Icon className="w-3 h-3" />{flat ? "flat vs last month" : `${delta >= 0 ? "+" : "−"}${Math.abs(delta)}${suffix} vs last month`}
        </p>
      )}
    </div>
  );
}
